/**
 * Политика RPC для FILEATTACHMENTS: upload перед getFullData, inline getData(actionId).
 * WUI-316: блокировки Р7 (`isAttachmentR7EditBlocked` / `isAttachmentR7OpenBlocked`),
 * `canOpen*` для кнопки редактора, `shouldUploadDsStagedAttachmentOnGetFullData`.
 *
 * @see dev/WUI-293.md — «Уточнение логики MFileInput»
 * @see dev/WUI-316.md §4.6
 */
import type { FileItem } from '@/common/types/fileItem'
import { isOfficeFileName } from '@/common/utils/attachmentPreviewUtils'
import {
  isAttachmentItemChanged,
  isNewLocalAttachmentItem,
  getAttachmentClientRowKey,
  getAttachmentStorageUri,
  getScalarFromFields,
  resolveAttachmentRowCodeVersion,
  rowHasServerCode,
  uniDataTableToFileItems,
} from '@/common/utils/fileAttachmentsUniData'
import { getDsAttachmentStore } from '@/common/composables/useDsAttachmentState'
import { isDsAttachmentOpenBlockedPhase } from '@/common/utils/dsAttachmentPhases'
import type { DsAttachmentPhase } from '@/common/utils/dsAttachmentPhases'
import {
  findSessionForAttachmentItem,
  resolveAttachmentDocumentSessionId,
} from '@/common/utils/r7AttachmentEditSession'
import { collectPageActionsFromForm } from '@/common/utils/collectPageActionsFromForm'
import { t } from '@/common/utils/translations'
import { hasNativeFileForAttachmentRow } from '@/common/utils/attachmentNativeFileRegistry'

/** Атрибуты PreviewBean / FileAttribute в исходящей строке (boolean). */
export const FILE_ATTACHMENT_ATTRIBUTE_KEYS = [
  'private',
  'unremovable',
  'tmpUnremovable',
  'uneditable',
  'unsendable',
  'indescribable',
  'requiredSignature',
  'stampable',
  'externalEditing',
  'forbidDownload',
] as const

export type FileAttachmentsElementMeta = {
  /** XML `<protection>`: upload запрещён при значении `reading`. */
  protection?: string
  /** XML `<isReadOnly>1</isReadOnly>` на элементе. */
  elementReadOnly?: boolean
}

export type FileAttachmentsActionLookup = {
  /** Actions из XML формы (`pageData.form.actions.action`). */
  pageActions?: readonly unknown[] | null
  /** Runtime registry (`GlobalProvider.actions`). */
  registeredActions?: Record<string, unknown> | null
}

/** Actions из `pageData.form.actions.action` (как `usePageData.pageActions`). */
export function collectFormActionsFromPageData(pageData: unknown): readonly unknown[] {
  return collectPageActionsFromForm((pageData as { form?: unknown } | null)?.form)
}

export function parseFileAttachmentsElementMeta(elementConfig: unknown): FileAttachmentsElementMeta {
  const el = elementConfig as Record<string, { _text?: string }> | null | undefined
  const protection = String(el?.protection?._text ?? '').trim().toLowerCase()
  const isReadOnly = String(el?.isReadOnly?._text ?? '').trim()
  return {
    protection: protection || undefined,
    elementReadOnly: isReadOnly === '1' || isReadOnly.toLowerCase() === 'true',
  }
}

/** Строка подписана или помечена uneditable — read-only для upload (дизъюнкция). */
export function isAttachmentRowReadOnly(item: FileItem): boolean {
  const fields = (item.fields ?? {}) as Record<string, unknown>
  if (isTruthyField(fields.uneditable)) return true
  return signatureFileListHasRows(fields.signature_file_list)
}

/** FileAttribute.forbidDownload: кнопка скачать неактивна, GET /platform/download на middle запрещён. */
export function isAttachmentDownloadForbidden(item: FileItem): boolean {
  const fields = (item.fields ?? {}) as Record<string, unknown>
  return isTruthyField(fields.forbidDownload)
}

function isTruthyField(field: unknown): boolean {
  if (field == null) return false
  if (typeof field === 'boolean') return field
  if (typeof field === 'object' && field !== null && 'value' in field) {
    const v = (field as { value: unknown }).value
    return v === true || v === 'true' || v === '1' || v === 1
  }
  return false
}

function signatureFileListHasRows(signatureField: unknown): boolean {
  if (signatureField == null || typeof signatureField !== 'object') return false
  const value =
    'value' in signatureField
      ? (signatureField as { value: unknown }).value
      : signatureField
  if (!value || typeof value !== 'object') return false
  const rows = (value as { rows?: unknown[] }).rows
  return Array.isArray(rows) && rows.length > 0
}

/** Элемент read-only по XML (`isReadOnly=1`) — блокирует любые операции с содержимым. */
export function isElementReadOnlyByMeta(meta: FileAttachmentsElementMeta): boolean {
  return meta.elementReadOnly === true
}

export type FileAttachmentsUiContext = {
  elementMeta: FileAttachmentsElementMeta
  /** Runtime `is_editable` / `setEditable` (как `VisualEntity.isEditable()` в Swing). */
  elementEditable?: boolean
  panelReadOnly?: boolean
  formReadOnly?: boolean
}

/**
 * Блок мутации ленты в UI: добавление, удаление, DnD, карточка «+».
 * Как Swing `setEditable(false)` → `serviceButtons` и контекстное меню скрыты.
 *
 * `protection=reading` сюда не входит: в Swing тулбар остаётся, блокируется только upload в `getData`.
 */
export function isFileAttachmentsTapeMutationBlocked(ctx: FileAttachmentsUiContext): boolean {
  if (ctx.formReadOnly) return true
  if (ctx.panelReadOnly) return true
  if (ctx.elementEditable === false) return true
  if (isElementReadOnlyByMeta(ctx.elementMeta)) return true
  return false
}

/**
 * Блок upload перед getFullData (WUI-293): `isReadOnly=1` или `protection=reading`.
 * Как Swing: `getData` не шлёт файлы при `FileProtectionMode.reading`.
 */
export function isElementUploadBlockedByMeta(meta: FileAttachmentsElementMeta): boolean {
  if (meta.elementReadOnly) return true
  return meta.protection === 'reading'
}

/** Блок edit в Р7: read-only элемент или режим защиты документа (Swing: `canBeSaved`, `setFinal`). */
export function isElementR7EditBlockedByMeta(meta: FileAttachmentsElementMeta): boolean {
  if (meta.elementReadOnly) return true
  return meta.protection === 'reading'
}

/** Коды невыполненных условий upload перед getFullData (конъюнкция WUI-293). */
export type AttachmentUploadSkipReason =
  | 'element_protection_reading'
  | 'element_is_read_only'
  | 'no_local_file'
  | 'has_server_code'
  | 'changed_false'
  | 'row_read_only_uneditable'
  | 'row_read_only_signed'

export { getAttachmentStorageUri } from '@/common/utils/fileAttachmentsUniData'

/** DS-снимок или R7-сессия ждут финальный WS после закрытия редактора с правками. */
export function isAttachmentR7PreviewAwaitingFinalSave(
  item: FileItem,
  elementId?: number | null,
): boolean {
  const eid = elementId != null ? Number(elementId) : NaN
  if (Number.isFinite(eid) && eid > 0) {
    try {
      if (getDsAttachmentStore(eid).getPhase(item) === 'awaiting_ws') return true
    } catch {
      // ignore invalid elementId
    }
  }
  const session = findSessionForAttachmentItem(item, elementId)
  return session?.phase === 'awaiting_ws'
}

/** Переводит DS-снимок в `awaiting_ws` (dirty в Р7 / close с правками). */
export function setDsAttachmentAwaitingWs(item: FileItem, elementId: number): void {
  if (!Number.isFinite(elementId) || elementId <= 0) return
  try {
    const store = getDsAttachmentStore(elementId)
    if (store.hasSnapshot(item)) {
      store.setPhase(item, 'awaiting_ws')
    }
  } catch {
    // ignore
  }
}

/** Снимок DS для строки (без throw при невалидном elementId). */
function getDsSnapshotSafe(item: FileItem, elementId?: number | null) {
  const eid = elementId != null ? Number(elementId) : NaN
  if (!Number.isFinite(eid) || eid <= 0) return undefined
  try {
    return getDsAttachmentStore(eid).getSnapshot(item)
  } catch {
    return undefined
  }
}

function getDsPhaseSafe(item: FileItem, elementId?: number | null): DsAttachmentPhase | 'none' {
  const eid = elementId != null ? Number(elementId) : NaN
  if (!Number.isFinite(eid) || eid <= 0) return 'none'
  try {
    return getDsAttachmentStore(eid).getPhase(item)
  } catch {
    return 'none'
  }
}

type AttachmentR7EditBlockReasonKey = 'awaiting_ws' | 'error'

/** Блок getFullData: DS-фаза — SSOT; session — fallback при отсутствии снимка. */
function resolveAttachmentR7EditBlockState(
  item: FileItem,
  elementId?: number | null,
): { blocked: boolean; reasonKey: AttachmentR7EditBlockReasonKey | null } {
  const session = findSessionForAttachmentItem(item, elementId)
  const dsPhase = getDsPhaseSafe(item, elementId)

  if (dsPhase !== 'none') {
    if (dsPhase === 'awaiting_ws' || dsPhase === 'downloading') {
      return { blocked: true, reasonKey: 'awaiting_ws' }
    }
    if (dsPhase === 'error') {
      return { blocked: true, reasonKey: 'error' }
    }
    if (dsPhase === 'staged' || dsPhase === 'idle' || dsPhase === 'discarded') {
      // Устаревшая session.awaiting_ws после staged не блокирует promote.
      return { blocked: false, reasonKey: null }
    }
    if (dsPhase === 'editing') {
      const blocked =
        session?.wasDirty === true
        || session?.phase === 'awaiting_ws'
        || session?.phase === 'error'
      if (!blocked) return { blocked: false, reasonKey: null }
      return {
        blocked: true,
        reasonKey: session?.phase === 'error' ? 'error' : 'awaiting_ws',
      }
    }
  }

  if (session?.phase === 'awaiting_ws') {
    return { blocked: true, reasonKey: 'awaiting_ws' }
  }
  if (session?.phase === 'error') {
    return { blocked: true, reasonKey: 'error' }
  }
  return { blocked: false, reasonKey: null }
}

/** Блок getFullData: незавершённое save-ожидание или terminal error (§4.6). */
export function isAttachmentR7EditBlocked(
  item: FileItem,
  _meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): boolean {
  return resolveAttachmentR7EditBlockState(item, elementId).blocked
}

/**
 * Instant getFullData abort (toast + throw): only DS wait / error.
 * Dirty `editing` is wait-for-prepare (forcesave/close), not an instant abort —
 * otherwise form-save onClick never reaches execute_procedure.
 */
export function isAttachmentR7GetFullDataHardBlocked(
  item: FileItem,
  _meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): boolean {
  const dsPhase = getDsPhaseSafe(item, elementId)
  if (dsPhase !== 'none') {
    return dsPhase === 'awaiting_ws' || dsPhase === 'downloading' || dsPhase === 'error'
  }
  const session = findSessionForAttachmentItem(item, elementId)
  return session?.phase === 'awaiting_ws' || session?.phase === 'error'
}

function isDsSnapshotOpenBlocked(item: FileItem, elementId?: number | null): boolean {
  const eid = elementId != null ? Number(elementId) : NaN
  if (!Number.isFinite(eid) || eid <= 0) return false
  try {
    return isDsAttachmentOpenBlockedPhase(getDsAttachmentStore(eid).getPhase(item))
  } catch {
    return false
  }
}

/** Блок open/view/preview в Р7: фазы DS-снимка (WUI-344 §6) и `awaiting_ws`. */
export function isAttachmentR7OpenBlocked(
  item: FileItem,
  elementId?: number | null,
): boolean {
  if (isDsSnapshotOpenBlocked(item, elementId)) return true
  const session = findSessionForAttachmentItem(item, elementId)
  return session?.phase === 'awaiting_ws'
}

/** Сообщение для toast/verificationError при блокировке getFullData. */
export function getAttachmentR7EditBlockReason(
  item: FileItem,
  elementId?: number | null,
): string | null {
  const state = resolveAttachmentR7EditBlockState(item, elementId)
  if (!state.blocked) return null
  if (state.reasonKey === 'error') {
    return t('attachments.r7EditSaveFailed')
  }
  return t('attachments.r7WaitFormSave')
}

/**
 * Promote temp с middleware вместо повторной заливки `File`.
 * Только после Р7-save: `dsDirty` + `staged` + `generation > 0` (WUI-344 §7).
 * Idle после prepare preview без правок — обычный upload с `File` в multipart.
 */
export function shouldPromoteAttachmentViaDsSnapshot(
  item: FileItem,
  elementId?: number | null,
): boolean {
  const eid = elementId != null ? Number(elementId) : NaN
  if (!Number.isFinite(eid) || eid <= 0) return false

  const snapshot = getDsSnapshotSafe(item, elementId)
  if (!snapshot?.storageUri?.trim() || !snapshot?.documentSessionId?.trim()) return false

  const phase = snapshot.phase
  if (phase === 'awaiting_ws' || phase === 'downloading' || phase === 'error') return false

  return Boolean(snapshot.dsDirty && phase === 'staged' && snapshot.generation > 0)
}

/** Строка со staged DS-снимком (`dsDirty`) — R7 promote upload на getFullData (WUI-344 §7). */
export function shouldUploadDsStagedAttachmentOnGetFullData(
  item: FileItem,
  meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): boolean {
  if (isAttachmentR7EditBlocked(item, meta, elementId)) return false
  if (isElementUploadBlockedByMeta(meta)) return false
  if (isAttachmentRowReadOnly(item)) return false

  return shouldPromoteAttachmentViaDsSnapshot(item, elementId)
}

/** Какие условия upload не выполнены; пустой массив — upload разрешён. */
export function getAttachmentUploadSkipReasons(
  item: FileItem,
  meta: FileAttachmentsElementMeta,
): AttachmentUploadSkipReason[] {
  const reasons: AttachmentUploadSkipReason[] = []
  if (meta.elementReadOnly) {
    reasons.push('element_is_read_only')
  } else if (meta.protection === 'reading') {
    reasons.push('element_protection_reading')
  }
  if (!(item.file instanceof File)) {
    reasons.push('no_local_file')
  }
  if (rowHasServerCode((item.fields ?? {}) as Record<string, unknown>)) {
    reasons.push('has_server_code')
  }
  if (!isAttachmentItemChanged(item)) {
    reasons.push('changed_false')
  }
  const fields = (item.fields ?? {}) as Record<string, unknown>
  if (isTruthyField(fields.uneditable)) {
    reasons.push('row_read_only_uneditable')
  } else if (signatureFileListHasRows(fields.signature_file_list)) {
    reasons.push('row_read_only_signed')
  }
  return reasons
}

/**
 * Upload строки перед getFullData — все условия (конъюнкция) из спецификации WUI-293.
 */
export function resolveAttachmentDisplayFileName(item: FileItem): string {
  if (item.file instanceof File) {
    return item.file.name
  }
  const fields = (item.fields ?? {}) as Record<string, unknown>
  const raw = fields.name ?? fields.fileName
  if (typeof raw === 'string') return raw
  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    return String((raw as { value: unknown }).value ?? '')
  }
  return ''
}

/** Кнопка «Открыть в Р7» (редактирование): серверная Office-строка или новый локальный Office-файл. */
export function canOpenAttachmentInR7Editor(
  item: FileItem,
  meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): boolean {
  if (isAttachmentR7OpenBlocked(item, elementId)) return false
  if (isElementR7EditBlockedByMeta(meta)) return false
  if (isAttachmentRowReadOnly(item)) return false

  const fileName = resolveAttachmentDisplayFileName(item)
  if (!fileName || !isOfficeFileName(fileName)) return false

  if (rowHasServerCode((item.fields ?? {}) as Record<string, unknown>)) return true
  return isNewLocalAttachmentItem(item)
}

/** Просмотр в Р7 (view mode) — read-only элемент/строка, `protection=reading` или новый локальный Office до upload. */
export function canViewAttachmentInR7Editor(
  item: FileItem,
  meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): boolean {
  if (isAttachmentR7OpenBlocked(item, elementId)) return false

  const fileName = resolveAttachmentDisplayFileName(item)
  if (!fileName || !isOfficeFileName(fileName)) return false

  const fields = (item.fields ?? {}) as Record<string, unknown>
  const hasServerCode = rowHasServerCode(fields)

  if (isElementReadOnlyByMeta(meta)) {
    return hasServerCode
  }

  if (isAttachmentRowReadOnly(item)) {
    return hasServerCode
  }

  if (meta.protection === 'reading') {
    if (hasServerCode) return true
    return isNewLocalAttachmentItem(item)
  }

  return false
}

/** Редактирование или просмотр в Р7; учитывает `isAttachmentR7OpenBlocked` (только `awaiting_ws`). */
export function canOpenOrViewAttachmentInR7Editor(
  item: FileItem,
  meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): boolean {
  return (
    canOpenAttachmentInR7Editor(item, meta, elementId) ||
    canViewAttachmentInR7Editor(item, meta, elementId)
  )
}

/**
 * Inline preview в зоне превью MFileInput (WUI-317 + WUI-344 §6).
 * Блокируется при `awaiting_ws` / `editing` / `error` через `isAttachmentR7OpenBlocked`.
 */
export function canPreviewAttachmentInR7Inline(
  item: FileItem,
  meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): boolean {
  if (isAttachmentR7OpenBlocked(item, elementId)) return false

  const fileName = resolveAttachmentDisplayFileName(item)
  if (!fileName || !isOfficeFileName(fileName)) return false

  const fields = (item.fields ?? {}) as Record<string, unknown>
  if (rowHasServerCode(fields)) return true
  return !isAttachmentRowReadOnly(item) && isNewLocalAttachmentItem(item)
}

/** Режим DocEditor: view для read-only строк, иначе edit. */
export function resolveAttachmentR7EditorMode(
  item: FileItem,
  meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): 'edit' | 'view' {
  if (canOpenAttachmentInR7Editor(item, meta, elementId)) return 'edit'
  if (canViewAttachmentInR7Editor(item, meta, elementId)) return 'view'
  return 'edit'
}

export function shouldUploadAttachmentOnGetFullData(
  item: FileItem,
  meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): boolean {
  if (shouldUploadDsStagedAttachmentOnGetFullData(item, meta, elementId)) {
    return true
  }
  const reasons = getAttachmentUploadSkipReasons(item, meta)
  if (reasons.length === 0) return true
  const blocking = reasons.filter((reason) => reason !== 'no_local_file' && reason !== 'changed_false')
  if (blocking.length > 0) return false
  return hasNativeFileForAttachmentRow(item, elementId)
}

/**
 * Строка `is_refill` на ленте: метаданные с платформы, локальный `File` ещё не привязан.
 * Upload на getFullData начнётся после привязки файла (`findRefilledLocalRowForNativeFile`).
 */
export function isAttachmentRefillWireRowAwaitingFile(item: FileItem): boolean {
  if (item.file instanceof File) return false
  const fields = (item.fields ?? {}) as Record<string, unknown>
  if (rowHasServerCode(fields)) return false
  return resolveAttachmentDisplayFileName(item).trim().length > 0
}

/** Локальная строка без server `code`, ожидающая upload (в т.ч. `File` только в реестре). */
function isNewLocalAttachmentRowPendingUpload(item: FileItem): boolean {
  const fields = (item.fields ?? {}) as Record<string, unknown>
  if (rowHasServerCode(fields)) return false
  if (item.file instanceof File) {
    return isAttachmentItemChanged(item) || isNewLocalAttachmentItem(item)
  }
  if (getAttachmentClientRowKey(fields)) {
    const code = Number(getScalarFromFields(fields, 'code', '_id'))
    if (code === -1) return true
  }
  return isAttachmentRefillWireRowAwaitingFile(item)
}

/** Индикация на карточке ленты: строка будет загружена на платформу при следующем getFullData. */
export function isAttachmentRowPendingPlatformUpload(
  item: FileItem,
  meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): boolean {
  if (isElementUploadBlockedByMeta(meta)) return false
  if (isAttachmentRowReadOnly(item)) return false
  if (shouldUploadAttachmentOnGetFullData(item, meta, elementId)) return true
  return isNewLocalAttachmentRowPendingUpload(item)
}

/**
 * Pending на платформу, но нет native `File` (ни на строке, ни в registry) и это не promote.
 * File в registry — upload, не throw. `file://` без session — не recovery (wait preview / toast после).
 */
export function isAttachmentPendingUploadMissingLocalFile(
  item: FileItem,
  meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): boolean {
  if (item.file instanceof File) return false
  if (hasNativeFileForAttachmentRow(item, elementId)) return false
  if (shouldUploadDsStagedAttachmentOnGetFullData(item, meta, elementId)) return false
  if (!isAttachmentRowPendingPlatformUpload(item, meta, elementId)) return false
  return Boolean(
    resolveAttachmentDocumentSessionId(item, elementId) || getAttachmentStorageUri(item),
  )
}

/** Таблица getFullData/putFullData всё ещё с pending без server code — нельзя отдать `code=-1`. */
export function fileAttachmentsTableHasPendingUploadWithoutServerCode(
  tableField: unknown,
  meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): boolean {
  return uniDataTableToFileItems(tableField).some((item) =>
    isAttachmentPendingUploadMissingLocalFile(item, meta, elementId),
  )
}

/**
 * Pending на page/forms таблице после pickPreferred ≠ «upload уже пробовали».
 * Throw только после overlay + повторных prepare/getFullData.
 */
export function fileAttachmentsPutFullDataShouldThrowPending(options: {
  overlayRetryDone: boolean
  tableStillPending: boolean
}): boolean {
  return options.overlayRetryDone && options.tableStillPending
}

/**
 * `actionId` из WS args: TypeValuePair `{ type: 'Long', value }` или число.
 * Возвращает целое или `null`.
 */
export function parseAttachmentGetDataActionId(arg: unknown): number | null {
  if (arg === undefined || arg === null) return null
  if (typeof arg === 'number' && Number.isFinite(arg)) return Math.trunc(arg)
  if (typeof arg === 'string') {
    const trimmed = arg.trim()
    if (!trimmed || Number.isNaN(Number(trimmed))) return null
    return Math.trunc(Number(trimmed))
  }
  if (typeof arg === 'object' && arg !== null) {
    const pair = arg as { type?: string; value?: unknown }
    if ('value' in pair) {
      return parseAttachmentGetDataActionId(pair.value)
    }
  }
  return null
}

function actionDefinitionId(action: unknown): string | null {
  if (action == null || typeof action !== 'object') return null
  const a = action as Record<string, unknown>
  const raw =
    (a._attributes as { id?: string | number } | undefined)?.id ??
    a.id ??
    (a.data as { _attributes?: { id?: string | number } } | undefined)?._attributes?.id
  if (raw === undefined || raw === null || raw === '') return null
  return String(raw).trim()
}

/** `layout.level=element` и `layout.position=inline` (как в `InlineActions.vue`). */
export function isInlineElementActionDefinition(action: unknown): boolean {
  if (action == null || typeof action !== 'object') return false
  const data =
    (action as { data?: unknown }).data != null &&
    typeof (action as { data?: unknown }).data === 'object'
      ? (action as { data: Record<string, unknown> }).data
      : (action as Record<string, unknown>)
  const layout = data.layout as
    | { level?: { _text?: string }; position?: { _text?: string } }
    | undefined
  return layout?.level?._text === 'element' && layout?.position?._text === 'inline'
}

export function findFormActionDefinitionById(
  actionId: number,
  lookup?: FileAttachmentsActionLookup | null,
): unknown | null {
  const idStr = String(actionId)
  const pageActions = lookup?.pageActions
  if (pageActions?.length) {
    for (const action of pageActions) {
      if (actionDefinitionId(action) === idStr) return action
    }
  }
  const registered = lookup?.registeredActions
  if (!registered) return null
  const direct = registered[actionId] ?? registered[idStr]
  if (direct) return (direct as { data?: unknown }).data ?? direct
  for (const entry of Object.values(registered)) {
    if (actionDefinitionId(entry) === idStr) {
      return (entry as { data?: unknown }).data ?? entry
    }
  }
  return null
}

/**
 * Inline getData: в args передан `actionId`, и action в форме — element + inline.
 */
export function shouldUseInlineAttachmentGetData(
  actionArg: unknown,
  lookup?: FileAttachmentsActionLookup | null,
): boolean {
  const actionId = parseAttachmentGetDataActionId(actionArg)
  if (actionId == null) return false
  const definition = findFormActionDefinitionById(actionId, lookup)
  return isInlineElementActionDefinition(definition)
}
